import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { API_BASE as API } from '../lib/apiBase';

const DEFAULT_GOOGLE_CLIENT_ID = '1044310266037-1fbg8osq8eb88cidi21g379ofa3ceu9o.apps.googleusercontent.com';

export default function LoginPage() {
  const { t, lang } = useLang();
  const { login, register, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'user', birth_date: '' });
  const [consents, setConsents] = useState({ accept_terms: false, accept_privacy: false, accept_artist_terms: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');

  const postLoginPath = (u) => {
    if (u?.role === 'legal_advisor') return '/legal-advisor';
    return u?.onboarding_completed === false ? '/onboarding' : '/';
  };

  useEffect(() => {
    fetch(`${API}/api/admin/settings/public`).then(r => r.json()).then(d => {
      setGoogleClientId(d.google_client_id || process.env.REACT_APP_GOOGLE_CLIENT_ID || DEFAULT_GOOGLE_CLIENT_ID);
    }).catch(() => {
      setGoogleClientId(process.env.REACT_APP_GOOGLE_CLIENT_ID || DEFAULT_GOOGLE_CLIENT_ID);
    });
  }, []);

  useEffect(() => {
    if (!googleClientId) return;

    const initGoogle = () => {
      const target = document.getElementById('google-signin-btn');
      if (!window.google || !target) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleGoogleLogin,
      });
      window.google.accounts.id.renderButton(target, {
        theme: 'filled_black',
        size: 'large',
        width: '100%',
        text: 'signin_with',
        shape: 'pill',
      });
    };

    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing && window.google) {
      initGoogle();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = initGoogle;
    document.head.appendChild(script);
  }, [googleClientId]);

  const handleGoogleLogin = async (response) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/google`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential })
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (res.ok && data.token) {
        loginWithToken(data.token, data.user);
        navigate(postLoginPath(data.user));
      } else {
        const msg = data.detail || (lang === 'es' ? 'Error con Google' : 'Google sign-in failed');
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
    } catch (e) {
      setError(lang === 'es' ? 'No se pudo contactar con el servidor. Revisa la conexión.' : 'Could not reach the server.');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        const data = await register({ ...form, role: 'user', ...consents, signer_name: form.name });
        navigate(postLoginPath(data.user));
      } else {
        const data = await login(form.email, form.password);
        navigate(postLoginPath(data.user));
      }
    } catch (err) {
      setError(err.message || (lang === 'es' ? 'Error al iniciar sesion' : 'Login failed'));
    }
    setLoading(false);
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-pulse-primary mx-auto flex items-center justify-center mb-4">
            <span className="font-heading font-extrabold text-2xl text-black">P</span>
          </div>
          <h1 className="font-heading font-bold text-2xl">PulseMusic</h1>
          <p className="text-pulse-muted text-sm mt-1">
            {isRegister ? t('auth.register') : t('auth.login')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegister && (
            <div className="relative">
              <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-pulse-muted" />
              <input
                data-testid="register-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder={t('auth.name')}
                className="w-full bg-pulse-surface border border-pulse-border rounded-xl pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                required
              />
            </div>
          )}

          <div className="relative">
            <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-pulse-muted" />
            <input
              data-testid="login-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder={t('auth.email')}
              className="w-full bg-pulse-surface border border-pulse-border rounded-xl pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              required
            />
          </div>

          <div className="relative">
            <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-pulse-muted" />
            <input
              data-testid="login-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder={t('auth.password')}
              className="w-full bg-pulse-surface border border-pulse-border rounded-xl pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              required
            />
          </div>

          {isRegister && (
            <div className="relative">
              <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-pulse-muted" />
              <input
                data-testid="register-birthdate"
                type="date"
                value={form.birth_date}
                onChange={(e) => setForm(f => ({ ...f, birth_date: e.target.value }))}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                className="w-full bg-pulse-surface border border-pulse-border rounded-xl pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none text-pulse-muted"
                required
                title={lang === 'es' ? 'Fecha de nacimiento (mayores de 18)' : 'Date of birth (18+ only)'}
              />
              <span className="text-[10px] text-pulse-muted mt-1 ml-1">{lang === 'es' ? 'Fecha de nacimiento (mayores de 18)' : 'Date of birth (18+ required)'}</span>
            </div>
          )}

          {isRegister && (
            <div data-testid="register-legal-consents" className="space-y-3 rounded-2xl border border-pulse-border bg-pulse-surface px-4 py-4">
              <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3 text-xs text-cyan-100 leading-relaxed">
                {lang === 'es'
                  ? 'En el siguiente paso te ayudaremos a elegir si vienes a descubrir música o a construir tu perfil artista.'
                  : 'In the next step we will help you choose whether you are here to discover music or build your artist identity.'}
              </div>
              <label className="flex items-start gap-3 text-sm text-pulse-muted">
                <input data-testid="consent-terms-checkbox" type="checkbox" checked={consents.accept_terms} onChange={e => setConsents(prev => ({ ...prev, accept_terms: e.target.checked }))} className="mt-1" />
                <span>{lang === 'es' ? 'Acepto los' : 'I accept the'} <a href="/legal?page=terms" className="text-pulse-primary hover:underline">{lang === 'es' ? 'Términos y Condiciones' : 'Terms and Conditions'}</a></span>
              </label>
              <label className="flex items-start gap-3 text-sm text-pulse-muted">
                <input data-testid="consent-privacy-checkbox" type="checkbox" checked={consents.accept_privacy} onChange={e => setConsents(prev => ({ ...prev, accept_privacy: e.target.checked }))} className="mt-1" />
                <span>{lang === 'es' ? 'Acepto la' : 'I accept the'} <a href="/legal?page=privacy" className="text-pulse-primary hover:underline">{lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}</a></span>
              </label>
            </div>
          )}

          {error && (
            <p data-testid="auth-error" className="text-red-400 text-sm text-center">{error}</p>
          )}

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-pulse-primary text-black font-bold py-3 rounded-xl hover:bg-pulse-primary-hover transition-colors duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                {isRegister ? t('auth.register') : t('auth.login')}
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        {/* Forgot password */}
        {!isRegister && (
          <div className="text-center mt-3 space-y-2">
            <div>
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-pulse-muted hover:text-pulse-primary transition-colors">{lang === 'es' ? 'He olvidado mi contrasena' : 'Forgot password?'}</Link>
            </div>
            <div>
              <Link to="/activate-access" data-testid="first-access-link" className="text-xs text-pulse-primary hover:underline">
                {lang === 'es' ? 'Primera sesion: recibir enlace seguro por email' : 'First access: receive secure link by email'}
              </Link>
            </div>
          </div>
        )}

        {/* Age verification notice for registration */}
        {isRegister && (
          <p className="text-[10px] text-pulse-muted text-center mt-2">{lang === 'es' ? 'Al registrarte confirmas que eres mayor de 18 anos y aceptas los ' : 'By registering you confirm you are 18+ and accept our '}<a href="/legal?page=terms" className="text-pulse-primary hover:underline">{lang === 'es' ? 'Terminos' : 'Terms'}</a>{' '}{lang === 'es' ? 'y la' : 'and'}{' '}<a href="/legal?page=privacy" className="text-pulse-primary hover:underline">{lang === 'es' ? 'Politica de Privacidad' : 'Privacy Policy'}</a></p>
        )}

        {/* Google Sign In */}
        {googleClientId && (
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-pulse-border" />
              <span className="text-xs text-pulse-muted">{lang === 'es' ? 'o continuar con' : 'or continue with'}</span>
              <div className="flex-1 h-px bg-pulse-border" />
            </div>
            <div id="google-signin-btn" data-testid="google-signin-btn" className="flex justify-center" />
          </div>
        )}

        <p className="text-center text-sm text-pulse-muted mt-6">
          {isRegister ? (lang === 'es' ? '¿Ya tienes cuenta?' : 'Already have an account?') : (lang === 'es' ? '¿No tienes cuenta?' : "Don't have an account?")}
          <button
            data-testid="toggle-auth-mode"
            onClick={() => { setIsRegister(!isRegister); setError(''); }}
            className="text-pulse-primary ml-2 hover:underline"
          >
            {isRegister ? t('auth.login') : t('auth.register')}
          </button>
        </p>
      </div>
    </div>
  );
}
